import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const STATUS = ['confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [shipping, setShipping] = useState({});
  const [srStatus, setSrStatus] = useState(null);

  const load = () => api.get('/admin/orders').then(({ data }) => setOrders(data));

  useEffect(() => {
    load();
    api.get('/shipping/status').then(({ data }) => setSrStatus(data)).catch(() => {});
  }, []);

  const updateStatus = async (order_id, status) => {
    try { await api.put(`/admin/orders/${order_id}/status`, { status }); toast.success('Updated'); load(); }
    catch { toast.error('Update failed'); }
  };

  const shipNow = async (order_id) => {
    setShipping((s) => ({ ...s, [order_id]: true }));
    try {
      const { data } = await api.post(`/shipping/orders/${order_id}/ship`);
      if (data.already_shipped) {
        toast.info('Already shipped');
      } else {
        toast.success(`Shipped via ${data.shiprocket?.courier_name || 'Shiprocket'}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ship failed');
    } finally {
      setShipping((s) => ({ ...s, [order_id]: false }));
    }
  };

  return (
    <div>
      <h1 className="font-serif text-4xl text-sans_ink tracking-tight" data-testid="admin-orders-title">Orders</h1>
      <p className="text-sans_mute mt-2">Track and update customer orders.</p>

      {srStatus && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs bg-white border border-sans_line px-3 py-2" data-testid="shiprocket-status">
          <span className={`w-2 h-2 rounded-full ${srStatus.configured ? 'bg-emerald-600' : 'bg-neutral-400'}`} />
          <span className="text-sans_ink">Shiprocket</span>
          <span className="text-sans_mute">·</span>
          <span className="text-sans_mute">
            {srStatus.configured
              ? `${srStatus.auto_ship ? 'Auto-ship ON' : 'Manual'} · Pickup: ${srStatus.pickup_location}`
              : 'Not configured'}
          </span>
        </div>
      )}

      <div className="mt-8 bg-white border border-sans_line overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-sans_line">
            {['Order', 'Customer', 'Items', 'Total', 'Status', 'Shipping', 'Date'].map((h) => (
              <th key={h} className="text-left px-4 py-3 font-medium text-[11px] tracking-[0.18em] uppercase text-sans_mute">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-sans_mute">No orders yet.</td></tr>}
            {orders.map((o) => {
              const sr = o.shiprocket || {};
              const canShip = srStatus?.configured && !sr.awb_code;
              return (
                <tr key={o.order_id} className="border-b border-sans_line hover:bg-sans_bone/50 align-top" data-testid={`order-row-${o.order_id}`}>
                  <td className="px-4 py-3 text-sans_ink font-mono text-xs">{o.order_id}</td>
                  <td className="px-4 py-3 text-sans_ink">{o.user_email}</td>
                  <td className="px-4 py-3 text-sans_mute">{o.items?.length}</td>
                  <td className="px-4 py-3 text-sans_ink">₹{o.total}</td>
                  <td className="px-4 py-3">
                    <select value={o.status} onChange={(e) => updateStatus(o.order_id, e.target.value)} className="border border-sans_line px-2 py-1 text-sm bg-white" data-testid={`order-status-${o.order_id}`}>
                      {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {sr.awb_code ? (
                      <div className="space-y-1">
                        <div className="text-sans_ink">{sr.courier_name}</div>
                        <div className="text-sans_mute font-mono">AWB {sr.awb_code}</div>
                        {sr.track_url && <a href={sr.track_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline" data-testid={`order-track-${o.order_id}`}>Track</a>}
                        {sr.label_url && <a href={sr.label_url} target="_blank" rel="noreferrer" className="text-sans_ink underline ml-3">Label</a>}
                      </div>
                    ) : o.shiprocket_error ? (
                      <div className="space-y-1">
                        <div className="text-red-700 text-[11px]">Ship failed</div>
                        <button onClick={() => shipNow(o.order_id)} disabled={shipping[o.order_id]} className="text-sans_ink underline" data-testid={`order-retry-${o.order_id}`}>Retry</button>
                      </div>
                    ) : canShip ? (
                      <button
                        onClick={() => shipNow(o.order_id)}
                        disabled={shipping[o.order_id]}
                        className="bg-sans_ink text-sans_bone px-3 py-1.5 text-xs tracking-wide hover:opacity-90 disabled:opacity-50"
                        data-testid={`order-ship-${o.order_id}`}
                      >
                        {shipping[o.order_id] ? 'Shipping…' : 'Ship via Shiprocket'}
                      </button>
                    ) : (
                      <span className="text-sans_mute">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sans_mute text-xs whitespace-nowrap">{new Date(o.created_at).toLocaleDateString('en-IN')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOrders;
